import React, { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { CSSTransition } from "react-transition-group";

import classNames from "classnames";

import { hidePageAlert, showPageAlert } from "./lib/actions";
import pageAlertReducer from "./lib/reducers";

interface Props {
  /**
   * The custom icon className for an infoType alert. Defaults to "caprx-icon-formulary"
   */
  iconClass?: string;
  message: string;
  show: boolean;
  timeout?: number;
  type: string;
}

const PageAlertContainer: React.FC<Props> = ({
  type,
  message,
  show,
  timeout = 3000,
  iconClass = "caprx-icon-formulary",
}: Props): JSX.Element => {
  const dispatch = useDispatch();
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (show) {
      timerRef.current = setTimeout(() => {
        dispatch(hidePageAlert());
      }, timeout);
    }
    return () => clearTimeout(timerRef.current);
  }, [show, timeout, dispatch]);

  const successType = type === "success";
  const errorType = type === "error";
  const warningType = type === "warning";
  const infoType = type === "info";

  const pageAlertClass = classNames(
    "page-alert-wrapper",
    { success: successType },
    { error: errorType },
    { warning: warningType },
    { info: infoType },
  );

  const pageAlertIconClass = classNames(
    "page-alert-exit",
    { "caprx-icon-successcheck": successType },
    { "caprx-icon-fail": errorType },
    { "caprx-icon-warning": warningType },
    { [iconClass]: infoType },
  );

  return (
    <>
      <CSSTransition in={show} timeout={600} classNames="slide-down" appear unmountOnExit>
        <div className={pageAlertClass}>
          <span className={pageAlertIconClass} aria-hidden="true" />
          <span className="page-alert-message">{!message || typeof message === "string" ? message : String(message)}</span>
        </div>
      </CSSTransition>
    </>
  );
};

export { PageAlertContainer, showPageAlert, hidePageAlert, pageAlertReducer };
